import {
    findPredefinedThicknessIndex,
    predefinedPanelThicknesses,
    zFightingFixDistance,
} from "@dextall/panels-defaults";
import { CladdingCellStyle, type IPanelSource } from "../../../responses/panelSource";
import { BoundaryLoopsFactory } from "../boundaries/boundaryLoopsFactory";
import { editorCladdingGeometryThickness } from "../defaults";
import { FacadeElementType } from "./facadeElementType";
import { PanelLouvre } from "./panelLouvre";
import { PanelWindow } from "./panelWindow";
import { WallPanelCladdingCell } from "./wallPanelCladdingCell";
import type { IPanelLouvreCutouts, IPanelType, IPanelWindowDesignParameters } from "../../../responses/panelType";
import type { SystemSettings } from "../../../responses/systemSettings";
import type { BoundaryLoop } from "../boundaries/boundaryLoop";
import type {
    CladdingCellExtrusionGeometryFactory,
    ExtrusionGeometry,
} from "../geometry/claddingCellExtrusionGeometryFactory";
import type { CardinalDirection } from "../geometry/compass";
import type { Depth, InstallationSequence, PanelParameters } from "./panelParameters";

export class Panel {
    private readonly _depth: Depth;
    private _elementName: string;
    private _installationSequence: InstallationSequence;

    constructor(
        private readonly panelSource: IPanelSource,
        private readonly panelBoundaryLoop: BoundaryLoop,
        systemSetting: SystemSettings,
        public readonly panelDbId: number,
        claddingExtrusionFactory: CladdingCellExtrusionGeometryFactory,
        boundaryLoopsFactory: BoundaryLoopsFactory,
        glassMaterial: THREE.Material,
        private readonly panelBoundaryPlaneMaterial: THREE.Material,
        public readonly facadeOrientation: CardinalDirection,
    ) {
        this.claddingCells = panelSource.claddings.map(
            x =>
                new WallPanelCladdingCell(
                    x,
                    panelBoundaryLoop.transform,
                    claddingExtrusionFactory,
                    boundaryLoopsFactory,
                ),
        );

        this.setCladdingCellsCustomMaterial(!!panelSource.customPanelTypeId);

        this.windows = panelSource.openings.map(x =>
            PanelWindow.create(
                panelSource.box.min,
                x,
                systemSetting,
                panelBoundaryLoop.transform,
                claddingExtrusionFactory,
                boundaryLoopsFactory,
                glassMaterial,
                this.style,
            ),
        );

        this.louvres = panelSource.louvres.map(x =>
            PanelLouvre.create(
                panelSource.box.min,
                x,
                systemSetting,
                panelBoundaryLoop.transform,
                claddingExtrusionFactory,
                boundaryLoopsFactory,
                this.style,
            ),
        );

        this._elementName = panelSource.elementName;
        this._installationSequence = panelSource.installationSequence;
        this._depth = panelSource.depth;

        for (const claddingCell of this.claddingCells) {
            claddingCell.assignId(this.panelDbId);
        }
    }

    get id(): string {
        return this.panelSource.id;
    }

    get elementType(): FacadeElementType.StandardPanel {
        return FacadeElementType.StandardPanel;
    }

    get installationSequence(): InstallationSequence {
        return this._installationSequence;
    }

    get depth(): Depth {
        return this._depth;
    }

    get panelTypeId(): string {
        return this.panelSource.panelTypeId;
    }

    get customPanelTypeId(): string | null {
        return this.panelSource.customPanelTypeId;
    }

    get userUniqueId(): number {
        return this.panelSource.userUniqueId;
    }

    get elementName(): string {
        return this._elementName;
    }

    get wallFaceTransform(): THREE.Matrix4 {
        return this.panelBoundaryLoop.transform;
    }

    get box(): THREE.Box3 {
        return this.panelBoundaryLoop.box;
    }

    get length(): number {
        return this.box.getSize().x;
    }

    get height(): number {
        return this.box.getSize().y;
    }

    get wallFaceId(): string {
        return this.panelSource.wallFaceId;
    }

    get offsetX(): number {
        return this.box.min.x;
    }

    get offsetY(): number {
        return this.box.min.y;
    }

    get thicknessLabel(): string {
        const index = findPredefinedThicknessIndex(this.panelSource.thickness);

        const predefinedThickness = predefinedPanelThicknesses[index];

        return predefinedThickness.name;
    }

    get style(): CladdingCellStyle {
        return this.claddingCells[0]?.style ?? CladdingCellStyle.Exposed;
    }

    get isParapetPanel(): boolean {
        return this.panelSource.isParapetPanel;
    }

    public readonly claddingCells: WallPanelCladdingCell[];

    public readonly windows: PanelWindow[];

    public readonly louvres: PanelLouvre[];

    createGeometry(): PanelGeometry {
        const claddingCellsGeometry = this.claddingCells.map(x => x.createGeometry());
        const windowsGeometry = this.windows.flatMap(x => x.createGeometry());
        const louvresGeometry = this.louvres.flatMap(x => x.createGeometry());

        return {
            claddingCellsGeometry,
            windowsGeometry,
            louvresGeometry,
            panelBoundaryGeometry: this.createPanelBoundaryGeometry(),
        };
    }

    update({ elementName, installationSequence }: PanelParameters) {
        this._elementName = elementName;
        this._installationSequence = installationSequence;
    }

    setPanelType(newPanelType: IPanelType, isRegularType: boolean) {
        if (isRegularType) {
            this.panelSource.panelTypeId = newPanelType.id;
        } else {
            this.panelSource.customPanelTypeId = newPanelType.id;
        }

        this.setCladdingCellsCustomMaterial(!!this.customPanelTypeId || newPanelType.withoutCladdings);
    }

    setPanelWindowDesignParameters(index: number, parameters: IPanelWindowDesignParameters) {
        const panelWindow = this.windows[index];

        panelWindow.area = parameters.area;
        panelWindow.weight = parameters.weight;
    }

    setLouvreCutouts(index: number, cutouts: IPanelLouvreCutouts) {
        const panelLouvre = this.louvres[index];

        panelLouvre?.setCutouts(cutouts);
    }

    setCustomPanelType(customPanelTypeId: string | null) {
        this.panelSource.customPanelTypeId = customPanelTypeId;

        this.setCladdingCellsCustomMaterial(!!customPanelTypeId);
    }

    computePanelTypeTitleMatrix(titleSize: THREE.Vector3) {
        const panelTransform = this.panelBoundaryLoop.transform;

        const panelBox = this.panelBoundaryLoop.box;

        const position = new THREE.Vector3(
            0.5 * (panelBox.min.x + panelBox.max.x - titleSize.x),
            0.5 * (panelBox.min.y + panelBox.max.y - titleSize.y),
            editorCladdingGeometryThickness + zFightingFixDistance,
        ).applyMatrix4(panelTransform);

        return new THREE.Matrix4().copy(panelTransform).setPosition(position);
    }

    hasWindows(): boolean {
        return this.windows.length > 0;
    }

    getCurrentLocalCoordinateSystem(): THREE.Matrix4 {
        const coordinateSystem = new THREE.Matrix4().copy(this.panelBoundaryLoop.transform);

        const lowerLeftPoint = new THREE.Vector3().copy(this.panelBoundaryLoop.box.min).applyMatrix4(coordinateSystem);

        coordinateSystem.setPosition(lowerLeftPoint);

        return coordinateSystem;
    }

    getWorldOrigin(target?: THREE.Vector3): THREE.Vector3 {
        return this.panelBoundaryLoop.toWorldCoordinates(this.panelBoundaryLoop.box.min, target);
    }

    getWorldNormal(target?: THREE.Vector3): THREE.Vector3 {
        target = target || new THREE.Vector3();

        return target.setFromMatrixColumn(2, this.panelBoundaryLoop.transform);
    }

    private setCladdingCellsCustomMaterial(isCustomMaterial: boolean) {
        if (this.claddingCells[0]?.isCustomMaterial === isCustomMaterial) {
            return;
        }

        for (const claddingCell of this.claddingCells) {
            claddingCell.isCustomMaterial = isCustomMaterial;
        }
    }

    private createPanelBoundaryGeometry(): ExtrusionGeometry {
        const size = this.panelBoundaryLoop.box.getSize();

        const geometry = new THREE.PlaneBufferGeometry(size.x, size.y);

        const panelBoxCenter = this.panelBoundaryLoop.box.getCenter();

        const matrix = new THREE.Matrix4().multiplyMatrices(
            this.panelBoundaryLoop.transform,
            new THREE.Matrix4().setPosition(
                new THREE.Vector3().addVectors(panelBoxCenter, new THREE.Vector3(0, 0, zFightingFixDistance)),
            ),
        );

        return {
            type: "box",
            geometry,
            matrix,
            material: this.panelBoundaryPlaneMaterial,
            size,
        };
    }
}

export type PanelGeometry = {
    claddingCellsGeometry: ExtrusionGeometry[];
    windowsGeometry: ExtrusionGeometry[];
    louvresGeometry: ExtrusionGeometry[];
    panelBoundaryGeometry: ExtrusionGeometry;
};
